import React, { FC, useState } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  // Switch,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { DeleteModal } from 'src/components/Modals/Delete';
import { Page } from 'src/components/Page';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
// import { useAuthUrl } from 'src/hooks/useAuthUrl';
import { useConnectorAccount } from 'src/hooks/useConnectorAccount';
import {
  Account,
  AccountsContainer,
  ButtonsContainer,
  Cards,
  ConnectorCard,
  ConnectorCardHeader,
  Description,
  ExpandMore,
  InnerContainer,
  PropertiesContainer,
  Wrapper,
} from 'src/pages/ConnectorAccounts/styled';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { isConnectorType } from 'src/utils';
import {
  createSelectAccountsMutation,
  DELETE_CONNECTOR,
  PLACEHOLDER_MUTATION,
} from 'src/utils/gql/mutations';
import {
  GET_CONNECTOR_SELECTIONS,
  GET_CONNECTORS,
} from 'src/utils/gql/queries';
import {
  Connector,
  ConnectorType,
  Mutation,
  MutationDeleteConnectorArgs,
} from 'src/utils/gql/types';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface FormattedConnector extends Connector {
  name: string;
}

export const ConnectorAccounts: FC = () => {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const connectorTypeParam = searchParams.get('type');
  const type = isConnectorType(connectorTypeParam)
    ? connectorTypeParam
    : ConnectorType.FACEBOOK;
  const { connectors, loading, connectorDetails, handleChangeConnectors } =
    useConnectorAccount({ type });
  // const { handleAddAccount, buttonLoading } = useAuthUrl({ type });

  const [open, setOpen] = useState(false);
  const [currentConnector, setCurrentConnector] =
    useState<FormattedConnector | null>(null);
  const [expandedConnectorId, setExpandedConnectorId] = useState('');

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors[0].message
        : errorText;

    snackbar(message);
  };

  const [deleteConnector, { loading: deletingConnector }] = useMutation<
    Pick<Mutation, 'deleteConnector'>,
    MutationDeleteConnectorArgs
  >(DELETE_CONNECTOR, {
    refetchQueries: [
      { query: GET_CONNECTORS, variables: { type } },
      { query: GET_CONNECTOR_SELECTIONS },
    ],
    onCompleted: () => {
      setOpen(false);
      snackbar(
        `${currentConnector?.name || ''} has been successfully deleted.`,
        'success',
      );
    },
    onError,
  });

  const supportsSelection = [
    ConnectorType.GOOGlE_ANALYTICS4,
    ConnectorType.GOOGLE_ADS,
    ConnectorType.FACEBOOK,
    ConnectorType.GOOGLE_SEARCH_CONSOLE,
  ].includes(type);

  const shouldCreateMutation = supportsSelection && connectors.length;

  const { mutationDocument, variables } = shouldCreateMutation
    ? createSelectAccountsMutation(connectors, type)
    : { mutationDocument: undefined, variables: undefined };

  const [selectAccounts, { loading: selectingAccounts }] = useMutation(
    mutationDocument || PLACEHOLDER_MUTATION,
    {
      variables,
      refetchQueries: [{ query: GET_CONNECTOR_SELECTIONS }],
      onError,
      onCompleted: () => {
        snackbar(`Accounts have been successfully updated.`, 'success');
        navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
      },
    },
  );

  const handleClickBack = () => navigate(`/${ROUTE_SOURCES.CONNECTORS}`);
  const handleClose = () => setOpen(false);

  const handleDeleteClick = (connetor: Connector, name: string) => {
    setOpen(true);
    setCurrentConnector({ ...connetor, name });
  };

  const handleDeleteConnector = () => {
    deleteConnector({ variables: { connectorId: currentConnector?.id || '' } });
  };

  const handleChangeAcc = (connectorId: string, accountId: string) => {
    const updatedConnectors = connectors.map((connector) => {
      if (connector.id !== connectorId) {
        return connector;
      }

      return {
        ...connector,
        accounts: connector.accounts.map((subAcc) => {
          const isSelected = subAcc.id === accountId ? !subAcc.selected : false;

          return {
            ...subAcc,
            selected: isSelected,
            properties: subAcc.properties.map((property) => ({
              ...property,
              selected: isSelected,
            })),
          };
        }),
      };
    });

    handleChangeConnectors(updatedConnectors);
  };

  const handleChangeProperty = (
    connectorId: string,
    accountId: string,
    propertyId: string,
  ) => {
    const updatedConnectors = connectors.map((connector) => {
      if (connector.id !== connectorId) {
        return connector;
      }

      return {
        ...connector,
        accounts: connector.accounts.map((account) => {
          const isTargetAcc = account.id === accountId;
          const updatedProperties = account.properties.map((property) => ({
            ...property,
            selected: isTargetAcc
              ? property.id === propertyId
                ? !property.selected
                : property.selected
              : false,
          }));
          const shouldSelectAcc =
            isTargetAcc &&
            updatedProperties.some((property) => property.selected);

          return {
            ...account,
            selected: shouldSelectAcc,
            properties: updatedProperties,
          };
        }),
      };
    });

    handleChangeConnectors(updatedConnectors);
  };

  const handleSubmit = () => {
    if (type !== ConnectorType.SHOPIFY) {
      selectAccounts();
    }
  };

  const toggleCollapse = (id: string) => {
    setExpandedConnectorId((prevId) => (prevId === id ? '' : id));
  };

  return (
    <Page maxWidth="800px">
      <Wrapper>
        {connectorDetails.icon}
        <Typography variant="h2" textAlign="center">
          {connectorDetails.title}
        </Typography>
        <Description variant="body2">
          {connectorDetails.description}
        </Description>
        <InnerContainer>
          <Cards>
            {loading && <CircularProgress />}
            {!loading && !connectors.length && (
              <Typography>No Accounts</Typography>
            )}
            {!loading &&
              connectors.map((connector, index) => (
                <ConnectorCard key={connector.id}>
                  <ConnectorCardHeader>
                    <Box
                      display="flex"
                      alignContent="center"
                      justifyContent="center"
                      gap="8px"
                    >
                      <Typography>
                        {connector.displayName || `Account${index + 1}`}
                      </Typography>
                      {!!connector.accounts.length && (
                        <ExpandMore
                          expand={+(expandedConnectorId === connector.id)}
                          onClick={() => toggleCollapse(connector.id)}
                        >
                          <ArrowDropDownIcon />
                        </ExpandMore>
                      )}
                    </Box>
                    <Box>
                      {/* <FormControlLabel control={<Switch defaultChecked />} label="Connected" labelPlacement="start" /> */}
                      <IconButton
                        onClick={() =>
                          handleDeleteClick(connector, `Account${index + 1}`)
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </ConnectorCardHeader>

                  <Collapse
                    in={expandedConnectorId === connector.id}
                    timeout="auto"
                    unmountOnExit
                  >
                    <AccountsContainer>
                      {connector.accounts.map((account) => (
                        <Account key={account.id}>
                          <Box p="10px 20px">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  icon={icon}
                                  checkedIcon={checkedIcon}
                                  checked={account.selected}
                                  onChange={() =>
                                    handleChangeAcc(connector.id, account.id)
                                  }
                                />
                              }
                              label={<Typography>{account.name}</Typography>}
                            />
                          </Box>
                          {!!account.properties?.length && (
                            <>
                              <Divider />
                              <PropertiesContainer>
                                {account.properties.map((property) => (
                                  <FormControlLabel
                                    key={property.id}
                                    control={
                                      <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        checked={property.selected}
                                        onChange={() =>
                                          handleChangeProperty(
                                            connector.id,
                                            account.id,
                                            property.id,
                                          )
                                        }
                                      />
                                    }
                                    label={
                                      <Typography variant="body2">
                                        {property.name}
                                      </Typography>
                                    }
                                  />
                                ))}
                              </PropertiesContainer>
                            </>
                          )}
                        </Account>
                      ))}
                    </AccountsContainer>
                  </Collapse>
                </ConnectorCard>
              ))}
            {/* <Button fullWidth variant="contained" color="secondary" onClick={handleAddAccount} loading={buttonLoading}>
              {connectorDetails.button}
            </Button> */}
          </Cards>
          <ButtonsContainer>
            <Button
              type="button"
              fullWidth
              variant="outlined"
              onClick={handleClickBack}
            >
              Back to connectors
            </Button>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || selectingAccounts}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </ButtonsContainer>
        </InnerContainer>
      </Wrapper>
      <DeleteModal
        open={open}
        name={currentConnector?.name || ''}
        title="Delete Connector?"
        loading={deletingConnector}
        onClose={handleClose}
        onDelete={handleDeleteConnector}
      />
    </Page>
  );
};
